import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import PageLayout from '../../components/feature/PageLayout';
import PageHero from '../../components/feature/PageHero';
import PageSectionNav from '../../components/feature/PageSectionNav';

interface Playlist {
  id: string;
  title: string;
}

const PLAYLISTS: Playlist[] = [
  { id: 'PL-pKieywUQRYqKZXSBkBpN2iLzLM65RW9', title: 'Father Anthony Micheal Arabic Sermons' },
  { id: 'PL-pKieywUQRZwkvYAkq-ux3qiNm372T_3', title: 'Father Anthony Micheal French Sermons' },
  { id: 'PL-pKieywUQRYIkFz3hypdS5aeZyns7ws8', title: 'Father Anthony Micheal English Sermons' },
  { id: 'PL-pKieywUQRbvHUEFqDFvxM1nX9CRNp2k', title: 'Guest Speakers Sermons' },
];

const API_KEY = 'AIzaSyCW_BcbpVi2SjK43I2jYN0e0IOyrlYJ45I';

export default function SermonsPage() {
  const { t } = useTranslation();
  const [playlistData, setPlaylistData] = useState<Record<string, any[]>>({});
  const [loading, setLoading] = useState(true);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [activeVideo, setActiveVideo] = useState<{ id: string; title: string } | null>(null);

  useEffect(() => {
    const fetchAllPlaylists = async () => {
      const results: Record<string, any[]> = {};
      const errorResults: Record<string, string> = {};

      await Promise.all(
        PLAYLISTS.map(async (playlist) => {
          try {
            const response = await fetch(
              `https://www.googleapis.com/youtube/v3/playlistItems?part=snippet&maxResults=5&playlistId=${playlist.id}&key=${API_KEY}`
            );

            if (!response.ok) {
              const errorData = await response.json();
              throw new Error(errorData.error?.message || 'Failed to fetch');
            }

            const data = await response.json();
            if (data.items && data.items.length > 0) {
              results[playlist.id] = data.items;
            } else {
              errorResults[playlist.id] = 'No videos found';
            }
          } catch (err: any) {
            console.error(`Error fetching playlist ${playlist.id}:`, err);
            errorResults[playlist.id] = err.message || 'Failed to load';
          }
        })
      );

      setPlaylistData(results);
      setErrors(errorResults);
      setLoading(false);
    };

    fetchAllPlaylists();
  }, []);

  const openVideo = (videoId: string, title: string) => {
    setActiveVideo({ id: videoId, title });
  };

  const closeVideo = useCallback(() => {
    setActiveVideo(null);
  }, []);

  useEffect(() => {
    if (!activeVideo) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeVideo();
    };
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', handleKey);
    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', handleKey);
    };
  }, [activeVideo, closeVideo]);

  return (
    <PageLayout>
      {/* Video Player Modal */}
      {activeVideo && (
        <div 
          className="fixed inset-0 z-[9999] flex items-center justify-center p-4 md:p-8"
          onClick={closeVideo}
        >
          <div className="absolute inset-0 bg-black/85 backdrop-blur-sm animate-fadeIn"></div>
          <div 
            className="relative z-10 w-full max-w-5xl animate-scaleIn"
            onClick={(e) => e.stopPropagation()}
          >
            <button 
              onClick={closeVideo}
              className="absolute -top-12 right-0 flex items-center gap-2 text-white/80 hover:text-white transition-colors cursor-pointer group"
            >
              <span className="text-sm font-medium opacity-0 group-hover:opacity-100 transition-opacity">{t('home.close')}</span>
              <div className="w-9 h-9 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 transition-colors">
                <i className="ri-close-line text-xl"></i>
              </div>
            </button>

            <div className="rounded-2xl overflow-hidden shadow-2xl bg-black">
              <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
                <iframe
                  className="absolute inset-0 w-full h-full"
                  src={`https://www.youtube.com/embed/${activeVideo.id}?autoplay=1&rel=0&modestbranding=1`}
                  title={activeVideo.title}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                ></iframe>
              </div>
            </div>

            <div className="mt-4 flex items-start justify-between gap-4">
              <h3 className="text-white font-semibold text-lg line-clamp-2 flex-1">{activeVideo.title}</h3>
              <a
                href={`https://www.youtube.com/watch?v=${activeVideo.id}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 px-4 py-2 bg-white/10 hover:bg-white/20 text-white text-sm font-medium rounded-full transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-youtube-line text-base"></i>
                {t('home.openOnYouTube')}
              </a>
            </div>
          </div>
        </div>
      )}

      <PageHero
        title={t('sermonsPage.title')}
        subtitle={t('sermonsPage.subtitle')}
        backgroundImage="/assets/readdy/9eda875d-3d3d-4284-9ae9-bca767912453_IMG_0496-2-67e8e63d7d3e.jpeg"
      />

      <PageSectionNav
        sections={PLAYLISTS.map((p) => ({ id: p.id, label: p.title }))}
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        {loading && (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-amber-600"></div>
            <p className="mt-4 text-gray-600">{t('sermons.loading')}</p>
          </div>
        )}

        {!loading && (
          <div className="space-y-16">
            {PLAYLISTS.map((playlist) => (
              <section key={playlist.id} id={playlist.id}>
                <div className="flex items-center justify-between mb-8">
                  <h2 className="text-2xl md:text-3xl font-serif font-bold text-gray-900">
                    {playlist.title}
                  </h2>
                  <a
                    href={`https://www.youtube.com/playlist?list=${playlist.id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 text-burgundy-700 hover:text-burgundy-800 font-medium text-sm cursor-pointer whitespace-nowrap"
                  >
                    {t('sermons.viewAll')}
                    <i className="ri-arrow-right-line"></i>
                  </a>
                </div>

                {errors[playlist.id] ? (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
                    <i className="ri-error-warning-line text-3xl text-red-500 mb-2"></i>
                    <p className="text-red-700">{errors[playlist.id]}</p>
                    <a
                      href={`https://www.youtube.com/playlist?list=${playlist.id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 mt-4 text-red-600 hover:text-red-700 font-medium cursor-pointer"
                    >
                      <i className="ri-youtube-line"></i>
                      {t('sermons.watchOnYouTube')}
                    </a>
                  </div>
                ) : playlistData[playlist.id] ? (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
                    {playlistData[playlist.id].map((video) => {
                      const snippet = video.snippet;
                      const videoId = snippet.resourceId?.videoId;
                      const thumbnail = snippet.thumbnails?.high?.url || snippet.thumbnails?.medium?.url || snippet.thumbnails?.default?.url;
                      const title = snippet.title;
                      const publishedAt = snippet.publishedAt;

                      return (
                        <div
                          key={video.id}
                          className="bg-white rounded-xl overflow-hidden shadow-md hover:shadow-xl transition-all cursor-pointer group"
                          onClick={() => openVideo(videoId, title)}
                        >
                          <div className="relative aspect-video overflow-hidden">
                            <img
                              src={thumbnail}
                              alt={title}
                              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                            />
                            <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                              <div className="w-12 h-12 flex items-center justify-center bg-white/90 rounded-full">
                                <i className="ri-play-fill text-2xl text-burgundy-700 ml-0.5"></i>
                              </div>
                            </div>
                          </div>
                          <div className="p-4">
                            <h3 className="text-sm font-semibold text-gray-900 mb-2 line-clamp-2 group-hover:text-burgundy-700 transition-colors leading-tight">
                              {title}
                            </h3>
                            <p className="text-xs text-gray-500">
                              {new Date(publishedAt).toLocaleDateString()}
                            </p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <i className="ri-video-line text-4xl text-gray-300 mb-2"></i>
                    <p className="text-gray-500">{t('sermons.noVideos')}</p>
                  </div>
                )}
              </section>
            ))}
          </div>
        )}
      </div>
    </PageLayout>
  );
}
